import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    Grid,
    Switch,
    FormControlLabel,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Slider,
    Divider,
    Card,
    CardContent,
    Chip,
    Tooltip,
    IconButton,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { showLoader } from '../../Redux/Slices/loaderSlice';
import { toast } from 'react-toastify';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import GridOnIcon from '@mui/icons-material/GridOn';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import axios from 'axios';
import { BaseURL, BucketURL } from '../../ApiSetup/Api';
import sessionStoreUtil from '../../ApiSetup/Localstorage';

const shapeOptions = [
    { value: 'circle', label: 'Circle' },
    { value: 'rectangle', label: 'Rectangle' },
    { value: 'rounded', label: 'Rounded Rectangle' },
    { value: 'custom', label: 'Custom Mask' },
];

const textAlignOptions = [
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' },
];

const defaultFormData = {
    name: '',
    nameGuj: '',
    category: '',
    width: 1080,
    height: 1080,
    photoConfig: {
        enabled: true,
        shape: 'circle',
        x: 440,
        y: 200,
        width: 200,
        height: 200,
        borderRadius: 0,
        borderColor: '#ffffff',
        borderWidth: 0,
    },
    nameConfig: {
        enabled: true,
        x: 540,
        y: 500,
        fontSize: 36,
        fontFamily: 'Arial',
        fontWeight: 'bold',
        color: '#000000',
        textAlign: 'center',
        useGujarati: false,
    },
    shareConfig: {
        defaultMessage: 'Check out my PPSK post! 🙏 #PPSK #PatidarParivarSurakshaKavach',
        defaultMessageGuj: '',
        hashtags: ['PPSK', 'PatidarParivarSurakshaKavach'],
        includeAppLink: true,
    },
    validFrom: '',
    validUntil: '',
    isDefault: false,
    isActive: true,
};

const ALLOWED_FILE_TYPES = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
const ALLOWED_EXTENSIONS = ['.svg', '.png', '.jpg', '.jpeg', '.webp'];

const TemplateEditor = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = !!id;

    const [categories, setCategories] = useState([]);
    const [formData, setFormData] = useState(defaultFormData);
    const [templateFile, setTemplateFile] = useState(null);
    const [templatePreview, setTemplatePreview] = useState(null);
    const [fileType, setFileType] = useState(null); // 'svg' or 'image'
    const [errors, setErrors] = useState({});
    
    // Interactive drag state
    const [isDragging, setIsDragging] = useState(null); // 'photo' or 'name'
    const [isResizing, setIsResizing] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const previewRef = useRef(null);
    const [previewDimensions, setPreviewDimensions] = useState({ width: 0, height: 0 });
    
    // Enhanced canvas controls
    const [showGrid, setShowGrid] = useState(true);
    const [zoomLevel, setZoomLevel] = useState(100);
    const [selectedElement, setSelectedElement] = useState(null); // 'photo' or 'name'
    const [showHelp, setShowHelp] = useState(false);

    const getAuthHeaders = () => {
        const token = localStorage.getItem("auth_token");
        return { Authorization: token };
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.post(
                `${BaseURL}admin/post-generator/category/get`,
                { includeInactive: false },
                { headers: getAuthHeaders() }
            );
            if (response?.data?.success) {
                setCategories(response?.data?.data || []);
            }
        } catch (error) {
            console.error('Fetch categories error:', error);
        }
    };

    const fetchTemplate = async () => {
        if (!id) return;
        dispatch(showLoader(true));
        try {
            const response = await axios.get(
                `${BaseURL}admin/post-generator/template/${id}`,
                { headers: getAuthHeaders() }
            );
            if (response?.data?.success) {
                const template = response.data.data;
                setFormData({
                    name: template.name || '',
                    nameGuj: template.nameGuj || '',
                    category: template.category?._id || template.category || '',
                    width: template.width || 1080,
                    height: template.height || 1080,
                    photoConfig: template.photoConfig || defaultFormData.photoConfig,
                    nameConfig: template.nameConfig || defaultFormData.nameConfig,
                    validFrom: template.validFrom ? template.validFrom.split('T')[0] : '',
                    validUntil: template.validUntil ? template.validUntil.split('T')[0] : '',
                    isDefault: template.isDefault || false,
                    isActive: template.isActive !== false,
                });
                if (template.previewImage) {
                    setTemplatePreview(template.previewImage.startsWith('http') 
                        ? template.previewImage 
                        : `${BucketURL}${template.previewImage}`);
                }
                if (template.shareConfig) {
                    setFormData(prev => ({ ...prev, shareConfig: template.shareConfig }));
                }
            }
        } catch (error) {
            console.error('Fetch template error:', error);
            toast.error('Failed to fetch template');
        } finally {
            dispatch(showLoader(false));
        }
    };

    useEffect(() => {
        const loadData = async () => {
            await fetchCategories();
            if (isEditMode) {
                fetchTemplate();
            }
        };
        loadData();
    }, [id]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handlePhotoConfigChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            photoConfig: { ...prev.photoConfig, [field]: value }
        }));
    };

    const handleNameConfigChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            nameConfig: { ...prev.nameConfig, [field]: value }
        }));
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const ext = '.' + file.name.split('.').pop().toLowerCase();
            if (!ALLOWED_EXTENSIONS.includes(ext) && !ALLOWED_FILE_TYPES.includes(file.type)) {
                toast.error('Please upload an image file (SVG, PNG, JPG, or WebP)');
                return;
            }
            setTemplateFile(file);
            setFileType(ext === '.svg' ? 'svg' : 'image');
            const reader = new FileReader();
            reader.onload = (e) => {
                setTemplatePreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleShareConfigChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            shareConfig: { ...prev.shareConfig, [field]: value }
        }));
    };

    const handleHashtagsChange = (value) => {
        const hashtags = value.split(',').map(tag => tag.trim()).filter(tag => tag);
        handleShareConfigChange('hashtags', hashtags);
    };

    // Update preview dimensions when image loads
    const handleImageLoad = useCallback((e) => {
        const img = e.target;
        setPreviewDimensions({ width: img.clientWidth, height: img.clientHeight });
    }, []);

    // Calculate position from mouse event relative to preview
    const getPositionFromEvent = useCallback((e) => {
        if (!previewRef.current) return null;
        const rect = previewRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        // Convert to template coordinates
        const templateX = Math.round((x / previewDimensions.width) * formData.width);
        const templateY = Math.round((y / previewDimensions.height) * formData.height);
        return { x: templateX, y: templateY };
    }, [previewDimensions, formData.width, formData.height]);

    // Handle drag start for photo or name
    const handleDragStart = useCallback((type, e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(type);
        const pos = getPositionFromEvent(e);
        if (pos) {
            if (type === 'photo') {
                setDragStart({ x: pos.x - formData.photoConfig.x, y: pos.y - formData.photoConfig.y });
            } else if (type === 'name') {
                setDragStart({ x: pos.x - formData.nameConfig.x, y: pos.y - formData.nameConfig.y });
            }
        }
    }, [getPositionFromEvent, formData.photoConfig.x, formData.photoConfig.y, formData.nameConfig.x, formData.nameConfig.y]);

    // Handle drag move
    const handleDragMove = useCallback((e) => {
        if (!isDragging) return;
        e.preventDefault();
        const pos = getPositionFromEvent(e);
        if (!pos) return;

        if (isDragging === 'photo') {
            const newX = Math.max(0, Math.min(formData.width - formData.photoConfig.width, pos.x - dragStart.x));
            const newY = Math.max(0, Math.min(formData.height - formData.photoConfig.height, pos.y - dragStart.y));
            setFormData(prev => ({
                ...prev,
                photoConfig: { ...prev.photoConfig, x: newX, y: newY }
            }));
        } else if (isDragging === 'name') {
            const newX = Math.max(0, Math.min(formData.width, pos.x - dragStart.x));
            const newY = Math.max(0, Math.min(formData.height, pos.y - dragStart.y));
            setFormData(prev => ({
                ...prev,
                nameConfig: { ...prev.nameConfig, x: newX, y: newY }
            }));
        }
    }, [isDragging, getPositionFromEvent, dragStart, formData.width, formData.height, formData.photoConfig.width, formData.photoConfig.height]);

    // Handle drag end
    const handleDragEnd = useCallback(() => {
        setIsDragging(null);
        setIsResizing(false);
    }, []);

    // Handle resize for photo
    const handleResizeStart = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsResizing(true);
        setIsDragging('photo-resize');
    }, []);

    const handleResizeMove = useCallback((e) => {
        if (!isResizing || isDragging !== 'photo-resize') return;
        e.preventDefault();
        const pos = getPositionFromEvent(e);
        if (!pos) return;

        const newWidth = Math.max(50, Math.min(formData.width - formData.photoConfig.x, pos.x - formData.photoConfig.x));
        const newHeight = Math.max(50, Math.min(formData.height - formData.photoConfig.y, pos.y - formData.photoConfig.y));
        
        // Keep aspect ratio if circle
        if (formData.photoConfig.shape === 'circle') {
            const size = Math.min(newWidth, newHeight);
            setFormData(prev => ({
                ...prev,
                photoConfig: { ...prev.photoConfig, width: size, height: size }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                photoConfig: { ...prev.photoConfig, width: newWidth, height: newHeight }
            }));
        }
    }, [isResizing, isDragging, getPositionFromEvent, formData.width, formData.height, formData.photoConfig.x, formData.photoConfig.y, formData.photoConfig.shape]);

    // Combined mouse move handler
    const handleMouseMove = useCallback((e) => {
        if (isDragging === 'photo-resize') {
            handleResizeMove(e);
        } else {
            handleDragMove(e);
        }
    }, [isDragging, handleResizeMove, handleDragMove]);

    // Add/remove event listeners
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleDragEnd);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleDragEnd);
            };
        }
    }, [isDragging, handleMouseMove, handleDragEnd]);

    // Helper function to center photo element
    const centerPhotoElement = () => {
        const centerX = Math.round((formData.width - formData.photoConfig.width) / 2);
        const centerY = Math.round((formData.height - formData.photoConfig.height) / 2);
        setFormData(prev => ({
            ...prev,
            photoConfig: { ...prev.photoConfig, x: centerX, y: centerY }
        }));
    };

    // Helper function to center name element
    const centerNameElement = () => {
        const centerX = Math.round(formData.width / 2);
        const centerY = Math.round(formData.height / 2);
        setFormData(prev => ({
            ...prev,
            nameConfig: { ...prev.nameConfig, x: centerX, y: centerY }
        }));
    };

    // Preset photo sizes
    const applyPhotoPreset = (preset) => {
        const presets = {
            small: { width: 150, height: 150 },
            medium: { width: 200, height: 200 },
            large: { width: 300, height: 300 },
            xlarge: { width: 400, height: 400 },
        };
        if (presets[preset]) {
            setFormData(prev => ({
                ...prev,
                photoConfig: { ...prev.photoConfig, ...presets[preset] }
            }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.category) newErrors.category = 'Category is required';
        if (!isEditMode && !templateFile) newErrors.file = 'Template image is required (SVG, PNG, JPG, or WebP)';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        dispatch(showLoader(true));
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('name', formData.name);
            formDataToSend.append('nameGuj', formData.nameGuj);
            formDataToSend.append('category', formData.category);
            formDataToSend.append('width', formData.width);
            formDataToSend.append('height', formData.height);
            formDataToSend.append('photoConfig', JSON.stringify(formData.photoConfig));
            formDataToSend.append('nameConfig', JSON.stringify(formData.nameConfig));
            formDataToSend.append('shareConfig', JSON.stringify(formData.shareConfig));
            formDataToSend.append('isDefault', formData.isDefault);
            formDataToSend.append('isActive', formData.isActive);
            
            if (formData.validFrom) formDataToSend.append('validFrom', formData.validFrom);
            if (formData.validUntil) formDataToSend.append('validUntil', formData.validUntil);
            
            if (templateFile) {
                formDataToSend.append('file', templateFile);
            }

            if (isEditMode) {
                formDataToSend.append('id', id);
                await axios.put(
                    `${BaseURL}admin/post-generator/template/update`,
                    formDataToSend,
                    { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
                );
                toast.success('Template updated successfully');
            } else {
                await axios.post(
                    `${BaseURL}admin/post-generator/template/create`,
                    formDataToSend,
                    { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
                );
                toast.success('Template created successfully');
            }

            navigate('/admin/post-generator/templates');
        } catch (error) {
            console.error('Submit error:', error);
            toast.error(error?.response?.data?.message || 'Failed to save template');
        } finally {
            dispatch(showLoader(false));
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/admin/post-generator/templates')}
                    >
                        Back
                    </Button>
                    <Typography variant="h5" fontWeight="bold">
                        {isEditMode ? 'Edit Template' : 'Add New Template'}
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    {/* Left Column - Form */}
                    <Grid item xs={12} md={7}>
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Basic Information</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Template Name"
                                            value={formData.name}
                                            onChange={(e) => handleChange('name', e.target.value)}
                                            error={!!errors.name}
                                            helperText={errors.name}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Name (Gujarati)"
                                            value={formData.nameGuj}
                                            onChange={(e) => handleChange('nameGuj', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth error={!!errors.category} required>
                                            <InputLabel>Category</InputLabel>
                                            <Select
                                                value={formData.category}
                                                label="Category"
                                                onChange={(e) => handleChange('category', e.target.value)}
                                            >
                                                {categories.map(cat => (
                                                    <MenuItem key={cat._id} value={cat._id}>
                                                        {cat.icon} {cat.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {errors.category && (
                                                <Typography variant="caption" color="error">{errors.category}</Typography>
                                            )}
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            label="Width (px)"
                                            type="number"
                                            value={formData.width}
                                            onChange={(e) => handleChange('width', parseInt(e.target.value) || 1080)}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            label="Height (px)"
                                            type="number"
                                            value={formData.height}
                                            onChange={(e) => handleChange('height', parseInt(e.target.value) || 1080)}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>📷 Photo Configuration</Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.photoConfig.enabled}
                                            onChange={(e) => handlePhotoConfigChange('enabled', e.target.checked)}
                                        />
                                    }
                                    label="Enable Photo Overlay"
                                />
                                {formData.photoConfig.enabled && (
                                    <Grid container spacing={2} sx={{ mt: 1 }}>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Shape</InputLabel>
                                                <Select
                                                    value={formData.photoConfig.shape}
                                                    label="Shape"
                                                    onChange={(e) => handlePhotoConfigChange('shape', e.target.value)}
                                                >
                                                    {shapeOptions.map(opt => (
                                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <TextField
                                                fullWidth
                                                label="X Position"
                                                type="number"
                                                value={formData.photoConfig.x}
                                                onChange={(e) => handlePhotoConfigChange('x', parseInt(e.target.value) || 0)}
                                            />
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <TextField
                                                fullWidth
                                                label="Y Position"
                                                type="number"
                                                value={formData.photoConfig.y}
                                                onChange={(e) => handlePhotoConfigChange('y', parseInt(e.target.value) || 0)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                label="Width"
                                                type="number"
                                                value={formData.photoConfig.width}
                                                onChange={(e) => handlePhotoConfigChange('width', parseInt(e.target.value) || 200)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                label="Height"
                                                type="number"
                                                value={formData.photoConfig.height}
                                                onChange={(e) => handlePhotoConfigChange('height', parseInt(e.target.value) || 200)}
                                            />
                                        </Grid>
                                        {formData.photoConfig.shape === 'rounded' && (
                                            <Grid item xs={12}>
                                                <Typography gutterBottom>Border Radius: {formData.photoConfig.borderRadius}px</Typography>
                                                <Slider
                                                    value={formData.photoConfig.borderRadius}
                                                    onChange={(e, val) => handlePhotoConfigChange('borderRadius', val)}
                                                    min={0}
                                                    max={100}
                                                />
                                            </Grid>
                                        )}
                                    </Grid>
                                )}
                            </CardContent>
                        </Card>

                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>📝 Name Configuration</Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.nameConfig.enabled}
                                            onChange={(e) => handleNameConfigChange('enabled', e.target.checked)}
                                        />
                                    }
                                    label="Enable Name Overlay"
                                />
                                {formData.nameConfig.enabled && (
                                    <Grid container spacing={2} sx={{ mt: 1 }}>
                                        <Grid item xs={6} sm={3}>
                                            <TextField
                                                fullWidth
                                                label="X Position"
                                                type="number"
                                                value={formData.nameConfig.x}
                                                onChange={(e) => handleNameConfigChange('x', parseInt(e.target.value) || 0)}
                                            />
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <TextField
                                                fullWidth
                                                label="Y Position"
                                                type="number"
                                                value={formData.nameConfig.y}
                                                onChange={(e) => handleNameConfigChange('y', parseInt(e.target.value) || 0)}
                                            />
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <TextField
                                                fullWidth
                                                label="Font Size"
                                                type="number"
                                                value={formData.nameConfig.fontSize}
                                                onChange={(e) => handleNameConfigChange('fontSize', parseInt(e.target.value) || 36)}
                                            />
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <TextField
                                                fullWidth
                                                label="Color"
                                                type="color"
                                                value={formData.nameConfig.color}
                                                onChange={(e) => handleNameConfigChange('color', e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Font Family"
                                                value={formData.nameConfig.fontFamily}
                                                onChange={(e) => handleNameConfigChange('fontFamily', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Text Align</InputLabel>
                                                <Select
                                                    value={formData.nameConfig.textAlign}
                                                    label="Text Align"
                                                    onChange={(e) => handleNameConfigChange('textAlign', e.target.value)}
                                                >
                                                    {textAlignOptions.map(opt => (
                                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={formData.nameConfig.useGujarati}
                                                        onChange={(e) => handleNameConfigChange('useGujarati', e.target.checked)}
                                                    />
                                                }
                                                label="Use Gujarati name if available"
                                            />
                                        </Grid>
                                    </Grid>
                                )}
                            </CardContent>
                        </Card>

                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Settings</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Valid From"
                                            type="date"
                                            value={formData.validFrom}
                                            onChange={(e) => handleChange('validFrom', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Valid Until"
                                            type="date"
                                            value={formData.validUntil}
                                            onChange={(e) => handleChange('validUntil', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={formData.isDefault}
                                                    onChange={(e) => handleChange('isDefault', e.target.checked)}
                                                />
                                            }
                                            label="Set as default template for this category"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={formData.isActive}
                                                    onChange={(e) => handleChange('isActive', e.target.checked)}
                                                />
                                            }
                                            label="Active"
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Right Column - Preview */}
                    <Grid item xs={12} md={5}>
                        <Card sx={{ position: 'sticky', top: 20 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>SVG Template</Typography>
                                
                                <Box
                                    sx={{
                                        border: '2px dashed #ccc',
                                        borderRadius: 2,
                                        p: 3,
                                        textAlign: 'center',
                                        mb: 2,
                                        cursor: 'pointer',
                                        '&:hover': { borderColor: 'primary.main' }
                                    }}
                                    onClick={() => document.getElementById('svg-upload').click()}
                                >
                                    <input
                                        id="svg-upload"
                                        type="file"
                                        accept=".svg,.png,.jpg,.jpeg,.webp,image/svg+xml,image/png,image/jpeg,image/webp"
                                        hidden
                                        onChange={handleFileChange}
                                    />
                                    <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                                    <Typography variant="body2" color="text.secondary">
                                        Click to upload template image (PNG, JPG, SVG, or WebP)
                                    </Typography>
                                    {templateFile && (
                                        <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                                            {templateFile.name}
                                        </Typography>
                                    )}
                                </Box>
                                {errors.file && (
                                    <Typography variant="caption" color="error">{errors.file}</Typography>
                                )}

                                {templatePreview && (
                                    <Box sx={{ mt: 2 }}>
                                        {/* Canvas Toolbar */}
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'space-between', 
                                            mb: 1,
                                            p: 1,
                                            bgcolor: '#f5f5f5',
                                            borderRadius: 1,
                                            flexWrap: 'wrap',
                                            gap: 1,
                                        }}>
                                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                                <Typography variant="subtitle2" sx={{ mr: 1 }}>Canvas:</Typography>
                                                <Tooltip title="Toggle Grid">
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => setShowGrid(!showGrid)}
                                                        color={showGrid ? 'primary' : 'default'}
                                                    >
                                                        <GridOnIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Zoom In">
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => setZoomLevel(Math.min(150, zoomLevel + 10))}
                                                        disabled={zoomLevel >= 150}
                                                    >
                                                        <ZoomInIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Chip label={`${zoomLevel}%`} size="small" sx={{ minWidth: 50 }} />
                                                <Tooltip title="Zoom Out">
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => setZoomLevel(Math.max(50, zoomLevel - 10))}
                                                        disabled={zoomLevel <= 50}
                                                    >
                                                        <ZoomOutIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <Tooltip title="Center Photo">
                                                    <Button 
                                                        size="small" 
                                                        variant="outlined"
                                                        startIcon={<CenterFocusStrongIcon sx={{ fontSize: 14 }} />}
                                                        onClick={centerPhotoElement}
                                                        disabled={!formData.photoConfig.enabled}
                                                        sx={{ fontSize: 10, py: 0.5 }}
                                                    >
                                                        Center Photo
                                                    </Button>
                                                </Tooltip>
                                                <Tooltip title="Center Name">
                                                    <Button 
                                                        size="small" 
                                                        variant="outlined"
                                                        startIcon={<TextFieldsIcon sx={{ fontSize: 14 }} />}
                                                        onClick={centerNameElement}
                                                        disabled={!formData.nameConfig.enabled}
                                                        sx={{ fontSize: 10, py: 0.5 }}
                                                    >
                                                        Center Name
                                                    </Button>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                        
                                        {/* Quick Size Presets */}
                                        {formData.photoConfig.enabled && (
                                            <Box sx={{ display: 'flex', gap: 0.5, mb: 1, alignItems: 'center' }}>
                                                <Typography variant="caption" sx={{ mr: 1 }}>Photo Size:</Typography>
                                                {['small', 'medium', 'large', 'xlarge'].map((size) => (
                                                    <Chip 
                                                        key={size}
                                                        label={size.charAt(0).toUpperCase() + size.slice(1)}
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => applyPhotoPreset(size)}
                                                        sx={{ 
                                                            fontSize: 10, 
                                                            cursor: 'pointer',
                                                            '&:hover': { bgcolor: 'primary.light', color: 'white' }
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                        
                                        {/* Help tip */}
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 1, 
                                            mb: 1, 
                                            p: 1, 
                                            bgcolor: 'rgba(33, 150, 243, 0.08)', 
                                            borderRadius: 1,
                                            border: '1px solid rgba(33, 150, 243, 0.2)'
                                        }}>
                                            <HelpOutlineIcon sx={{ fontSize: 16, color: 'info.main' }} />
                                            <Typography variant="caption" color="text.secondary">
                                                <strong>Tip:</strong> Drag the blue box to position photo, green box for name. Use corner handle to resize photo.
                                            </Typography>
                                        </Box>
                                        <Box
                                            ref={previewRef}
                                            sx={{
                                                border: '2px solid #3E4094',
                                                borderRadius: 1,
                                                overflow: 'auto',
                                                position: 'relative',
                                                cursor: isDragging ? 'grabbing' : 'default',
                                                userSelect: 'none',
                                                maxHeight: '60vh',
                                            }}
                                        >
                                            <Box sx={{ 
                                                transform: `scale(${zoomLevel / 100})`, 
                                                transformOrigin: 'top left',
                                                position: 'relative',
                                                width: `${100 * 100 / zoomLevel}%`,
                                            }}>
                                                <img
                                                    src={templatePreview}
                                                    alt="Template Preview"
                                                    style={{ width: '100%', display: 'block', pointerEvents: 'none' }}
                                                    onLoad={handleImageLoad}
                                                />
                                                {/* Grid Overlay */}
                                                {showGrid && (
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        backgroundImage: `
                                                            linear-gradient(rgba(0,0,0,0.05) 1px, transparent 1px),
                                                            linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px)
                                                        `,
                                                        backgroundSize: '10% 10%',
                                                        pointerEvents: 'none',
                                                    }} />
                                                )}
                                            {/* Photo placeholder - DRAGGABLE */}
                                            {formData.photoConfig.enabled && (
                                                <Tooltip title="Drag to move photo position, drag corner to resize" arrow>
                                                    <Box
                                                        onMouseDown={(e) => handleDragStart('photo', e)}
                                                        sx={{
                                                            position: 'absolute',
                                                            left: `${(formData.photoConfig.x / formData.width) * 100}%`,
                                                            top: `${(formData.photoConfig.y / formData.height) * 100}%`,
                                                            width: `${(formData.photoConfig.width / formData.width) * 100}%`,
                                                            height: `${(formData.photoConfig.height / formData.height) * 100}%`,
                                                            border: isDragging === 'photo' ? '3px solid #2196f3' : '2px dashed #2196f3',
                                                            borderRadius: formData.photoConfig.shape === 'circle' ? '50%' : 
                                                                formData.photoConfig.shape === 'rounded' ? `${formData.photoConfig.borderRadius}px` : 0,
                                                            cursor: 'grab',
                                                            backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            transition: isDragging ? 'none' : 'all 0.1s ease',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(33, 150, 243, 0.2)',
                                                                border: '3px solid #2196f3',
                                                            },
                                                            boxShadow: isDragging === 'photo' ? '0 0 10px rgba(33, 150, 243, 0.5)' : 'none',
                                                        }}
                                                    >
                                                        <PhotoCameraIcon sx={{ color: '#2196f3', fontSize: 20, opacity: 0.7 }} />
                                                        {/* Resize handle */}
                                                        <Box
                                                            onMouseDown={handleResizeStart}
                                                            sx={{
                                                                position: 'absolute',
                                                                right: -6,
                                                                bottom: -6,
                                                                width: 12,
                                                                height: 12,
                                                                backgroundColor: '#2196f3',
                                                                borderRadius: '50%',
                                                                cursor: 'se-resize',
                                                                border: '2px solid white',
                                                                '&:hover': {
                                                                    transform: 'scale(1.3)',
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                </Tooltip>
                                            )}
                                            {/* Name position - DRAGGABLE */}
                                            {formData.nameConfig.enabled && (
                                                <Tooltip title="Drag to move name position" arrow>
                                                    <Box
                                                        onMouseDown={(e) => handleDragStart('name', e)}
                                                        sx={{
                                                            position: 'absolute',
                                                            left: `${(formData.nameConfig.x / formData.width) * 100}%`,
                                                            top: `${(formData.nameConfig.y / formData.height) * 100}%`,
                                                            transform: 'translateX(-50%)',
                                                            cursor: 'grab',
                                                            padding: '4px 12px',
                                                            backgroundColor: isDragging === 'name' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.15)',
                                                            border: isDragging === 'name' ? '2px solid #4caf50' : '1px dashed #4caf50',
                                                            borderRadius: 1,
                                                            transition: isDragging ? 'none' : 'all 0.1s ease',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(76, 175, 80, 0.3)',
                                                                border: '2px solid #4caf50',
                                                            },
                                                            boxShadow: isDragging === 'name' ? '0 0 10px rgba(76, 175, 80, 0.5)' : 'none',
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <TextFieldsIcon sx={{ color: '#4caf50', fontSize: 14 }} />
                                                            <Typography 
                                                                sx={{ 
                                                                    color: formData.nameConfig.color,
                                                                    fontSize: 12,
                                                                    fontWeight: formData.nameConfig.fontWeight,
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                Sample Name
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Tooltip>
                                            )}
                                            </Box>
                                        </Box>
                                        {/* Position info */}
                                        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {formData.photoConfig.enabled && (
                                                <Chip 
                                                    size="small"
                                                    icon={<PhotoCameraIcon sx={{ fontSize: 12 }} />}
                                                    label={`Photo: ${formData.photoConfig.x}, ${formData.photoConfig.y} (${formData.photoConfig.width}×${formData.photoConfig.height})`}
                                                    sx={{ fontSize: 10, bgcolor: 'rgba(33, 150, 243, 0.1)' }}
                                                />
                                            )}
                                            {formData.nameConfig.enabled && (
                                                <Chip 
                                                    size="small"
                                                    icon={<TextFieldsIcon sx={{ fontSize: 12 }} />}
                                                    label={`Name: ${formData.nameConfig.x}, ${formData.nameConfig.y}`}
                                                    sx={{ fontSize: 10, bgcolor: 'rgba(76, 175, 80, 0.1)' }}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                )}

                                <Divider sx={{ my: 3 }} />

                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        onClick={() => navigate('/admin/post-generator/templates')}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={handleSubmit}
                                    >
                                        {isEditMode ? 'Update Template' : 'Create Template'}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
};

export default TemplateEditor;
